import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Send, Calendar, MessageSquare, Cake, PartyPopper, Wallet, ShoppingBag, Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTemplate = {
  name: '',
  channel: 'both',
  trigger_type: 'manual',
  audience: 'all',
  body: 'Dear {{name}}, ',
  festival_name: '',
  active: true,
};

const TRIGGER_META = {
  manual:          { label: 'Manual Blast',      icon: Send,       color: 'bg-stone-100 text-stone-700' },
  birthday:        { label: 'Birthday Auto',     icon: Cake,       color: 'bg-pink-100 text-pink-700' },
  festival:        { label: 'Festival Auto',     icon: PartyPopper,color: 'bg-amber-100 text-amber-700' },
  weekly_payment:  { label: 'Payment Trigger',   icon: Wallet,     color: 'bg-emerald-100 text-emerald-700' },
  purchase:        { label: 'Purchase Trigger',  icon: ShoppingBag,color: 'bg-blue-100 text-blue-700' },
};

const VARIABLES = ['{{name}}', '{{amount}}', '{{date}}', '{{festival}}', '{{product}}'];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('crm_token') || localStorage.getItem('token') || ''}` });

export default function MessagingCenter() {
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [logs, setLogs] = useState([]);
  const [bdays, setBdays] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(emptyTemplate);
  const [previewText, setPreviewText] = useState('');

  const [showFestModal, setShowFestModal] = useState(false);
  const [newFest, setNewFest] = useState({ name: '', date: '', note: '' });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, f, l, b] = await Promise.all([
        axios.get(`${API}/messaging/templates`, { headers: authHeaders() }),
        axios.get(`${API}/messaging/festivals`, { headers: authHeaders() }),
        axios.get(`${API}/messaging/logs?limit=200`, { headers: authHeaders() }),
        axios.get(`${API}/messaging/birthdays-today`, { headers: authHeaders() }),
      ]);
      setTemplates(t.data || []);
      setFestivals(f.data || []);
      setLogs(l.data || []);
      setBdays(b.data?.birthdays || []);
    } catch (e) {
      toast.error('Failed to load messaging data');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => { setEditing(emptyTemplate); setPreviewText(''); setShowModal(true); };
  const openEdit = (t) => { setEditing({ ...t }); setPreviewText(''); setShowModal(true); };

  const saveTemplate = async () => {
    if (!editing.name.trim() || !editing.body.trim()) {
      toast.error('Name and message body are required'); return;
    }
    try {
      if (editing.id) {
        await axios.put(`${API}/messaging/templates/${editing.id}`, editing, { headers: authHeaders() });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/messaging/templates`, editing, { headers: authHeaders() });
        toast.success('Template created');
      }
      setShowModal(false);
      loadAll();
    } catch { toast.error('Save failed'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await axios.delete(`${API}/messaging/templates/${id}`, { headers: authHeaders() });
    toast.success('Deleted');
    loadAll();
  };

  const sendNow = async (t) => {
    if (!window.confirm(`Queue this template to ALL "${t.audience}" recipients?`)) return;
    try {
      const r = await axios.post(`${API}/messaging/send/${t.id}`, {}, { headers: authHeaders() });
      toast.success(`Queued ${r.data.queued} messages (${r.data.channel}). Send provider will be wired later.`);
      loadAll();
    } catch { toast.error('Send failed'); }
  };

  const runPreview = async () => {
    try {
      const r = await axios.post(`${API}/messaging/preview`, { template: editing.body }, { headers: authHeaders() });
      setPreviewText(r.data.rendered || '');
    } catch { toast.error('Preview failed'); }
  };

  const insertVar = (v) => setEditing({ ...editing, body: (editing.body || '') + ' ' + v });

  const addFestival = async () => {
    if (!newFest.name || !newFest.date) { toast.error('Name and date required'); return; }
    try {
      await axios.post(`${API}/messaging/festivals`, newFest, { headers: authHeaders() });
      toast.success('Festival added');
      setShowFestModal(false);
      setNewFest({ name: '', date: '', note: '' });
      loadAll();
    } catch { toast.error('Add failed'); }
  };

  const delFestival = async (id) => {
    if (!window.confirm('Delete this festival?')) return;
    await axios.delete(`${API}/messaging/festivals/${id}`, { headers: authHeaders() });
    loadAll();
  };

  return (
    <div className="p-6 space-y-6" data-testid="messaging-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-emerald-800" />
            Messaging Center
          </h1>
          <p className="text-stone-600 text-sm mt-1">
            Templates · Auto-triggers · Festival Calendar · Message Log
          </p>
        </div>
        {bdays.length > 0 && (
          <div className="flex items-center gap-2 bg-pink-50 border border-pink-200 rounded-lg px-4 py-2" data-testid="birthday-banner">
            <Cake className="w-5 h-5 text-pink-600" />
            <span className="text-pink-800 font-semibold">{bdays.length} birthday{bdays.length > 1 ? 's' : ''} today</span>
          </div>
        )}
      </div>

      {/* Provider notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="provider-notice">
        <strong>Note:</strong> WhatsApp / SMS send provider is stubbed. All triggered messages are queued in the log
        with status <code className="bg-white px-1 rounded">queued_pending_provider</code>. Wire Twilio credentials later to send live.
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 max-w-2xl">
          <TabsTrigger value="templates" data-testid="tab-templates">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="festivals" data-testid="tab-festivals">Festivals ({festivals.length})</TabsTrigger>
          <TabsTrigger value="birthdays" data-testid="tab-birthdays">Today's Birthdays ({bdays.length})</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Message Log ({logs.length})</TabsTrigger>
        </TabsList>

        {/* TEMPLATES */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openCreate} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-template-btn">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          </div>
          {templates.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border border-stone-200 text-stone-500">
              No templates yet. Create your first bulk-message template.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(t => {
                const meta = TRIGGER_META[t.trigger_type] || TRIGGER_META.manual;
                const Icon = meta.icon;
                return (
                  <div key={t.id} className="bg-white border border-stone-200 rounded-lg p-4 hover:shadow-md transition" data-testid={`tpl-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-stone-900 truncate">{t.name}</h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge className={meta.color}><Icon className="w-3 h-3 mr-1" />{meta.label}</Badge>
                          <Badge variant="outline">{t.channel === 'both' ? 'WhatsApp + SMS' : t.channel.toUpperCase()}</Badge>
                          <Badge variant="outline">Audience: {t.audience}</Badge>
                          {!t.active && <Badge className="bg-red-100 text-red-700">INACTIVE</Badge>}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-stone-600 bg-stone-50 rounded p-2 line-clamp-3 whitespace-pre-wrap">{t.body}</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-tpl-${t.id}`}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => sendNow(t)} data-testid={`send-tpl-${t.id}`}><Send className="w-3 h-3 mr-1" />Queue Send</Button>
                      <Button size="sm" variant="outline" className="text-red-600 ml-auto" onClick={() => deleteTemplate(t.id)} data-testid={`del-tpl-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* FESTIVALS */}
        <TabsContent value="festivals" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowFestModal(true)} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-festival-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Festival
            </Button>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3 text-sm font-semibold text-stone-700">Date</th>
                  <th className="text-left p-3 text-sm font-semibold text-stone-700">Festival</th>
                  <th className="text-left p-3 text-sm font-semibold text-stone-700">Note</th>
                  <th className="text-right p-3 text-sm font-semibold text-stone-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {festivals.map(f => (
                  <tr key={f.id} className="border-t border-stone-100" data-testid={`fest-row-${f.id}`}>
                    <td className="p-3 text-sm"><Calendar className="w-3 h-3 inline mr-1 text-amber-600" />{f.date}</td>
                    <td className="p-3 font-medium">{f.name}</td>
                    <td className="p-3 text-sm text-stone-600">{f.note || '—'}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => delFestival(f.id)} data-testid={`del-fest-${f.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* BIRTHDAYS */}
        <TabsContent value="birthdays">
          {bdays.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border border-stone-200 text-stone-500">
              No birthdays today.
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-pink-50">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold text-stone-700">Name</th>
                    <th className="text-left p-3 text-sm font-semibold text-stone-700">Source</th>
                    <th className="text-left p-3 text-sm font-semibold text-stone-700">Phone</th>
                    <th className="text-left p-3 text-sm font-semibold text-stone-700">DOB</th>
                  </tr>
                </thead>
                <tbody>
                  {bdays.map((b, i) => (
                    <tr key={i} className="border-t border-stone-100" data-testid={`bday-row-${i}`}>
                      <td className="p-3 font-medium flex items-center gap-2"><Cake className="w-4 h-4 text-pink-600" />{b.name}</td>
                      <td className="p-3 text-sm"><Badge variant="outline">{b.source}</Badge></td>
                      <td className="p-3 text-sm">{b.phone || '—'}</td>
                      <td className="p-3 text-sm text-stone-600">{b.dob}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* LOGS */}
        <TabsContent value="logs">
          {logs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border border-stone-200 text-stone-500">
              No messages queued yet.
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold">When</th>
                    <th className="text-left p-3 text-sm font-semibold">Template</th>
                    <th className="text-left p-3 text-sm font-semibold">Recipient</th>
                    <th className="text-left p-3 text-sm font-semibold">Channel</th>
                    <th className="text-left p-3 text-sm font-semibold">Status</th>
                    <th className="text-left p-3 text-sm font-semibold">Body</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-t border-stone-100" data-testid={`log-row-${l.id}`}>
                      <td className="p-3 text-xs text-stone-500">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="p-3 text-sm font-medium">{l.template_name}</td>
                      <td className="p-3 text-sm">{l.recipient_name} <span className="text-stone-400 text-xs">({l.recipient_phone || 'no phone'})</span></td>
                      <td className="p-3 text-sm"><Badge variant="outline">{l.channel}</Badge></td>
                      <td className="p-3 text-sm"><Badge className="bg-amber-100 text-amber-800">{l.status}</Badge></td>
                      <td className="p-3 text-xs text-stone-700 max-w-[300px] truncate">{l.body}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Template modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing.id ? 'Edit Template' : 'New Message Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-stone-600">Template Name</label>
              <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Diwali 2026 Greeting" data-testid="tpl-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-600">Channel</label>
                <Select value={editing.channel} onValueChange={v => setEditing({ ...editing, channel: v })}>
                  <SelectTrigger data-testid="tpl-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">WhatsApp + SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp only</SelectItem>
                    <SelectItem value="sms">SMS only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Trigger</label>
                <Select value={editing.trigger_type} onValueChange={v => setEditing({ ...editing, trigger_type: v })}>
                  <SelectTrigger data-testid="tpl-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Blast</SelectItem>
                    <SelectItem value="birthday">Birthday (auto)</SelectItem>
                    <SelectItem value="festival">Festival (auto)</SelectItem>
                    <SelectItem value="weekly_payment">Weekly Payment (event)</SelectItem>
                    <SelectItem value="purchase">Purchase (event)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Audience</label>
                <Select value={editing.audience} onValueChange={v => setEditing({ ...editing, audience: v })}>
                  <SelectTrigger data-testid="tpl-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="employees">Employees</SelectItem>
                    <SelectItem value="customers">Customers</SelectItem>
                    <SelectItem value="cps">CPs (Channel Partners)</SelectItem>
                    <SelectItem value="barter">Barter Partners</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {editing.trigger_type === 'festival' && (
              <div>
                <label className="text-xs font-medium text-stone-600">Specific Festival (optional — leave blank to fire on ANY festival)</label>
                <Input value={editing.festival_name || ''} onChange={e => setEditing({ ...editing, festival_name: e.target.value })} placeholder="Diwali (Laxmi Pujan)" data-testid="tpl-festival-name" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-stone-600">Message Body</label>
              <Textarea rows={5} value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} data-testid="tpl-body" />
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-stone-500 self-center">Insert:</span>
                {VARIABLES.map(v => (
                  <button key={v} type="button" onClick={() => insertVar(v)} className="text-xs bg-stone-100 hover:bg-stone-200 px-2 py-1 rounded" data-testid={`insert-var-${v.replace(/[{}]/g,'')}`}>{v}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="tpl-active" />
              <label className="text-sm">Active (auto-triggers will fire)</label>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-stone-600">Preview</label>
                <Button size="sm" variant="outline" onClick={runPreview} data-testid="tpl-preview-btn">Run Preview</Button>
              </div>
              {previewText && <div className="text-sm bg-emerald-50 border border-emerald-200 rounded p-2 whitespace-pre-wrap">{previewText}</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={saveTemplate} data-testid="tpl-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Festival modal */}
      <Dialog open={showFestModal} onOpenChange={setShowFestModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Festival</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-stone-600">Name</label>
              <Input value={newFest.name} onChange={e => setNewFest({ ...newFest, name: e.target.value })} data-testid="fest-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Date</label>
              <Input type="date" value={newFest.date} onChange={e => setNewFest({ ...newFest, date: e.target.value })} data-testid="fest-date-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Note (optional)</label>
              <Input value={newFest.note} onChange={e => setNewFest({ ...newFest, note: e.target.value })} data-testid="fest-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFestModal(false)}>Cancel</Button>
            <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={addFestival} data-testid="fest-save-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
